/**
 * Alerta de identidade da planilha: o sistema reconhece cada ambiente por
 * "PEDIDO:AMBIENTE" (histórico de comentários, Mapa da Produção). Quando o
 * mesmo número de pedido aparece para clientes diferentes, ou o mesmo nome de
 * ambiente se repete dentro de um pedido, essa identidade quebra — e os
 * comentários desses ambientes ficam bloqueados até a planilha ser corrigida.
 * Some sozinho quando a planilha é corrigida.
 */
import React, { useMemo } from "react";
import styled from "styled-components";
import { FiAlertTriangle } from "react-icons/fi";

import { indiceDeIdentidade } from "../../utils/identidadeAmbiente";
import { plural } from "../../utils/dinheiro";

const Caixa = styled.section`
  margin: 12px 0;
  border: 1px solid var(--warning-border);
  background: var(--warning-bg);
  color: var(--warning-text);
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.45;

  h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 700;
  }

  p {
    margin: 0 0 6px;
  }

  details {
    margin-top: 6px;
  }

  summary {
    cursor: pointer;
    font-weight: 600;
    min-height: var(--tap-min, 32px);
    display: flex;
    align-items: center;
  }

  ul {
    margin: 4px 0 0;
    padding-left: 18px;
  }

  li {
    overflow-wrap: anywhere;
  }
`;

export default function AlertaIdentidade({ dados }) {
  const { pedidosComVariosClientes, ambientesRepetidos } = useMemo(() => indiceDeIdentidade(dados), [dados]);
  if (pedidosComVariosClientes.length === 0 && ambientesRepetidos.length === 0) return null;

  return (
    <Caixa role="status" aria-label="Problemas de identificação na planilha">
      <h2>
        <FiAlertTriangle aria-hidden="true" /> Corrija na planilha para não misturar ambientes
      </h2>
      <p>
        O sistema reconhece cada ambiente pelo número do pedido + nome do ambiente. Nos casos abaixo
        os comentários ficam bloqueados até a planilha ser corrigida.
      </p>
      {pedidosComVariosClientes.length > 0 && (
        <details open>
          <summary>
            {plural(
              pedidosComVariosClientes.length,
              "pedido com mais de um cliente",
              "pedidos com mais de um cliente"
            )}
          </summary>
          <ul>
            {pedidosComVariosClientes.map((item) => (
              <li key={item.pedidoNormalizado}>
                Pedido <strong>{item.pedido}</strong>: {item.clientes.join(" · ")}
              </li>
            ))}
          </ul>
        </details>
      )}
      {ambientesRepetidos.length > 0 && (
        <details open>
          <summary>
            {plural(
              ambientesRepetidos.length,
              "ambiente repetido no mesmo pedido",
              "ambientes repetidos no mesmo pedido"
            )}
          </summary>
          <ul>
            {ambientesRepetidos.map((item) => (
              <li key={item.chave}>
                <strong>{item.rotulo}</strong> ({item.cliente}) aparece em {item.linhas} linhas
              </li>
            ))}
          </ul>
        </details>
      )}
    </Caixa>
  );
}
